import { useEffect, useMemo, useState } from 'react'

import Image from 'next/image'

import Link from 'next/link'

import { useRouter } from 'next/router'

import { useTranslations } from 'next-intl'

import CateDropdown from '@/components/CateDropdown'
import FormGroup from '@/components/Form/FormGroup'
import { HeaderLogo } from '@/components/HeaderLogo'
import { HeaderMenu } from '@/components/Icon/HeaderMenu'
import { SearchBar } from '@/components/SearchBar'
import { TopHeader } from '@/components/TopHeader'
import { useNotification, useStore } from '@/containers'

interface HeaderLogo {
  label: JSX.Element | null
  key: number
}
type HeaderProps = {
  page?: string
}

const Header = ({ page }: HeaderProps) => {
  const router = useRouter()
  const [showAllCate, setShowAllCate] = useState(false)
  const t = useTranslations('Header')
  const { setOverlayMegaMenu, systemConfig } = useNotification()
  const { megaMenu, topBanner } = useStore()

  const cateDropdown = useMemo(() => {
    return (
      <CateDropdown
        className={`megaMenu container absolute left-1/2 top-full z-50 w-full -translate-x-1/2 overflow-hidden rounded-b-lg border-t border-grey-200 bg-white shadow-megaMenu`}
        items={megaMenu}
      />
    )
  }, [megaMenu, showAllCate, topBanner, router])

  const translateText = {
    allCate: 'Danh mục',
    promotion: t('promotion')
  }

  useEffect(() => {
    const handleStart = () => {
      setShowAllCate(false)
    }

    router.events.on('routeChangeComplete', handleStart)

    return () => {
      router.events.off('routeChangeComplete', handleStart)
    }
  }, [router])

  return (
    <header
      className='sticky top-0 z-[999999] border-b-2 border-grey-200 bg-white'
      onMouseLeave={() => {
        setShowAllCate(false)
        setOverlayMegaMenu(false)
      }}
    >
      {topBanner ? <TopHeader topBanner={topBanner} /> : ''}
      <div className={`container mx-auto h-20`}>
        <div className={`flex h-full items-center`}>
          {page && page == 'homepage' ? (
            <h1 className='max-w-[140px] flex-[140px] justify-center md:max-w-[180px] md:flex-[180px] lg:max-w-[240px] lg:flex-[240px]'>
              <HeaderLogo />
            </h1>
          ) : (
            <div className='max-w-[140px] flex-[140px] justify-center md:max-w-[180px] md:flex-[180px] lg:max-w-[240px] lg:flex-[240px]'>
              <HeaderLogo />
            </div>
          )}

          <div
            className='flex-355 flex h-full max-w-[355px] items-center'
            onMouseEnter={() => {
              setShowAllCate(true)
              setOverlayMegaMenu(true)
            }}
            onClick={() => {
              setShowAllCate(true)
              setOverlayMegaMenu(true)
            }}
          >
            <div className='flex h-12 w-full flex-1 cursor-pointer items-center justify-start gap-2 rounded-lg bg-grey-200 px-2'>
              <HeaderMenu />
              <span className='text-Headline font-normal text-dark-400'>{translateText.allCate}</span>
            </div>
            {showAllCate && cateDropdown}
          </div>

          <div className='relative ml-4 flex h-full max-w-[360px] flex-[360px] items-center md:max-w-[400px] md:flex-[400px] lg:max-w-[460px] lg:flex-[460px]'>
            <FormGroup className='flex w-full flex-row items-center rounded-lg bg-grey-200'>
              <SearchBar />
            </FormGroup>
          </div>
          <div className='flex h-full flex-1 items-center justify-end gap-5 font-semibold leading-tight xl:gap-4'>
            <Link href={systemConfig?.warehousePostSupport || '/'} className='flex items-center gap-1'>
              <Image src={'/assets/icons/header/support.svg'} width={18} height={18} alt='Hỗ trợ' />
              Hỗ trợ
            </Link>
            <Link href={'/post'} className='flex items-center gap-1'>
              <Image src={'/assets/icons/header/icon-news.svg'} width={18} height={18} alt='Tin tức' />
              Tin tức & Khuyến mãi
            </Link>
          </div>
        </div>
      </div>
    </header>
  )
}

export { Header }
